import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { businessOutline, chevronForward, storefrontOutline } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useCollettivaEnabled,
  useFoodCostEnabled,
  useEventQuotesEnabled,
  useTransportDocumentsEnabled,
  useTenantAiProvider,
  useAccountAccessState,
  useDashboardConfigState,
  useEvents,
  useMasters,
  useSetDashboardConfig,
  useStockProjection,
  useTenantAccounts,
  useUserBusinesses,
  useUserTenants,
  type DashboardConfig,
} from '../../lib/data/queries.js';
import { aiModelLabel, aiModelKind } from '@trackeat/shared';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { getSupabase } from '../../lib/supabase.js';
import { toUserMessage } from '../../lib/errors.js';
import { notifyExpirations } from '../../lib/notifications/expirationNotifier.js';
import { selectActiveProbeAlerts } from '../../lib/haccp/probeAlerts.js';
import { selectActionableExpirationAlerts } from '../../lib/haccp/expirationAlerts.js';
import {
  isWithinExpirationLead,
  useExpirationPreferences,
} from '../../lib/haccp/expirationPreferences.js';
import { AiSuggestionsFab } from '../../components/AiSuggestionsFab.js';
import { useAiSuggestions } from '../../lib/haccp/suggestions.js';
import {
  HeroTile,
  PageGreeting,
  SectionHeader,
  StatBadge,
  StatCard,
  StatRow,
  TileGrid,
  palette,
} from '../../components/design.js';
import { FeatureCard } from '../../components/FeatureCard.js';
import { DASHBOARD_TILES, isTileCapabilityEnabled, visibilitySectionsForCapabilities } from '../../lib/visibility/registry.js';
import {
  isSurfaceVisibleForSubjects,
  setPersonalSurfaceVisibility,
  setSurfaceVisibility,
} from '../../lib/visibility/isSurfaceVisible.js';
import { BusinessDirectoryModal } from '../../components/BusinessDirectoryModal.js';
import { buildBusinessDirectory, type BusinessDirectoryEntry } from '../../lib/auth/businessDirectory.js';
import { canConfigureCommercialPricing } from '../../lib/auth/capabilities.js';
import { useOperationalIdentity } from '../../lib/auth/useOperationalIdentity.js';

interface StorageUnit { id: string; name: string; temp_min_c: number | null; temp_max_c: number | null; check_frequency_hours: number | null }
interface ProbeAlert { id: string; client_ts: string; payload: { storage_unit_id: string; severity: string; message: string; window_end?: string | null } }
interface Food {
  id: string;
  name: string;
  default_uom?: string | null;
  recipe_id?: string | null;
}
// T-012: campi minimi business + tenant per considerare i dati locale "completi".
// La verifica del reminder usa lo stesso elenco; tenerli sincronizzati.
interface BusinessLite {
  id: string;
  // T-223: nome + tenant per il selettore "Locale" in Dashboard (un'azienda
  // con più locali deve poter switchare la sede attiva dalla home).
  tenant_id?: string | null;
  name?: string | null;
  archived_at?: string | null;
  piva: string | null;
  address: string | null;
  city: string | null;
  cap: string | null;
  provincia: string | null;
}
interface TenantLite {
  id: string;
  responsabile_haccp_name: string | null;
}

// T-248: l'hub "Export Registri" e i "Registri da compilare" sono ora pagine
// dedicate (RegistriPage / RegistriCompilaPage), raggiunte dai tile della
// dashboard, non più una sezione inline qui.

// Ruoli per cui il consulente può configurare la dashboard ('consulente' vede
// sempre tutto). Etichette in italiano.
const CONFIGURABLE_ROLES: { value: string; label: string }[] = [
  { value: 'ristoratore', label: 'Ristoratore' },
  { value: 'osa', label: 'OSA' },
  { value: 'staff', label: 'Staff' },
];

function chunkPairs<T>(arr: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += 2) out.push(arr.slice(i, i + 2));
  return out;
}

export function TodayPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile, switchTenant, switchBusiness } = useAuth();
  const history = useHistory();
  const collettivaEnabled = useCollettivaEnabled();
  const foodCostEnabled = useFoodCostEnabled();
  const eventQuotesEnabled = useEventQuotesEnabled();
  const transportDocumentsEnabled = useTransportDocumentsEnabled();
  const configurableVisibilitySections = useMemo(() => visibilitySectionsForCapabilities({
    collettiva: collettivaEnabled,
    foodCost: foodCostEnabled,
    eventQuotes: eventQuotesEnabled,
    transportDocuments: transportDocumentsEnabled,
  }), [collettivaEnabled, eventQuotesEnabled, foodCostEnabled, transportDocumentsEnabled]);
  const aiProvider = useTenantAiProvider();
  const expirationPreferences = useExpirationPreferences(profile?.active_business_id ?? null);

  // T-227: visibilità per-ruolo dei tile. Il consulente/owner vede tutto e può
  // aprire l'editor "Modifica dashboard".
  const dashboardState = useDashboardConfigState();
  const accountAccess = useAccountAccessState();
  const operationalIdentity = useOperationalIdentity();
  const dashboardConfig = dashboardState.config;
  const setDashboardConfig = useSetDashboardConfig();
  const currentRole = profile?.role ?? 'staff';
  const isConsultantOrOwner = currentRole === 'consulente' || profile?.is_platform_owner === true;
  const canConfigureOperationalProfiles = operationalIdentity.canManageOperationalWork;
  const canConfigurePricing = canConfigureCommercialPricing(profile);
  const { data: tenantAccounts = [] } = useTenantAccounts(canConfigureOperationalProfiles);
  const { data: staffProfileLinks = [] } = useMasters<{ id: string; user_id: string }>('staff_profiles');
  const configurableSubjects = useMemo(() => [
    ...CONFIGURABLE_ROLES.map((role) => ({ ...role, fallbackSubjects: [] as string[] })),
    ...operationalIdentity.personnel.map((person) => {
      const linkedUserId = staffProfileLinks.find((staff) => staff.id === person.staff_profile_id)?.user_id;
      const appRole = tenantAccounts.find((account) => account.user_id === linkedUserId)?.app_role;
      const fallbackRole = CONFIGURABLE_ROLES.some((role) => role.value === appRole) ? appRole! : 'staff';
      return {
        value: `person:${person.id}`,
        label: `${person.full_name} · ${person.role}`,
        fallbackSubjects: [fallbackRole],
      };
    }),
  ], [operationalIdentity.personnel, staffProfileLinks, tenantAccounts]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRole, setEditorRole] = useState<string>('ristoratore');
  const [draftConfig, setDraftConfig] = useState<DashboardConfig>({});
  const [savingDashboard, setSavingDashboard] = useState(false);

  // T-318: anteprima "Vedi come <ruolo>". Il consulente/owner vede SEMPRE tutto
  // (per design), quindi senza questo non avrebbe modo di verificare il masking
  // dal proprio account: simulando un ruolo vede la Home esattamente come la
  // vedrebbe quel ruolo, senza fare logout. `null` = nessuna anteprima.
  const [previewRole, setPreviewRole] = useState<string | null>(null);
  const effectiveSubjects = useMemo(() => previewRole
    ? [previewRole, ...(configurableSubjects.find((subject) => subject.value === previewRole)?.fallbackSubjects ?? [])]
    : operationalIdentity.subjects,
  [configurableSubjects, operationalIdentity.subjects, previewRole]);
  const effectiveIsConsultant = isConsultantOrOwner && previewRole === null;
  const visibilityResolved = dashboardState.resolved && (
    previewRole !== null || accountAccess.resolved
  );

  // Tile effettivamente mostrati: gating per flag (collettiva) + visibilità di
  // ruolo (il consulente/owner non viene mai filtrato, salvo in anteprima).
  const visibleTilePairs = useMemo(() => {
    if (!visibilityResolved && !effectiveIsConsultant) return [];
    const list = DASHBOARD_TILES.filter((tile) => {
      if (!isTileCapabilityEnabled(tile.flag, {
        collettiva: collettivaEnabled,
        foodCost: foodCostEnabled,
        eventQuotes: eventQuotesEnabled,
        transportDocuments: transportDocumentsEnabled,
      })) return false;
      if (!effectiveIsConsultant && !isSurfaceVisibleForSubjects(dashboardConfig, effectiveSubjects, tile.key)) return false;
      return true;
    });
    return chunkPairs(list);
  }, [collettivaEnabled, foodCostEnabled, eventQuotesEnabled, transportDocumentsEnabled, effectiveIsConsultant, dashboardConfig, visibilityResolved, effectiveSubjects]);

  // T-103: tenant (AZIENDA) switcher per consulente / platform owner con più tenant.
  const isMultiTenant =
    (profile?.global_role === 'consulente' || profile?.is_platform_owner === true) &&
    (profile?.tenants?.length ?? 0) > 1;
  const userTenantsState = useUserTenants();
  const userTenants = useMemo(() => userTenantsState.data ?? [], [userTenantsState.data]);
  const userBusinessesState = useUserBusinesses(isMultiTenant);
  const [businessDirectoryOpen, setBusinessDirectoryOpen] = useState(false);

  const { data: units = [] } = useMasters<StorageUnit>('storage_units');
  const { data: foods = [] } = useMasters<Food>('foods');
  const { data: businesses = [] } = useMasters<BusinessLite>('businesses');
  const { data: alerts = [] } = useEvents('probe_alert', 20);
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => !!food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom!.trim()]),
    ),
    [foods],
  );
  const stockProjection = useStockProjection({ canonicalUomByFood });

  const tenantId = profile?.active_tenant_id ?? null;

  // T-223: locali (business) dell'azienda attiva, non archiviati. Quando sono
  // più di uno, il selettore "Locale attivo" sotto permette di switchare la
  // sede dalla Dashboard (prima si poteva solo dalle Impostazioni) — per
  // QUALSIASI ruolo, non solo consulente/owner.
  const tenantBusinesses = useMemo(
    () => businesses
      .filter((b) => (b.tenant_id == null || b.tenant_id === tenantId) && !b.archived_at)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'it')),
    [businesses, tenantId],
  );
  const isMultiBusiness = tenantBusinesses.length > 1;
  const activeBusiness = businesses.find((b) => b.id === profile?.active_business_id) ?? null;
  const activeTenant = userTenants.find((tenant) => tenant.id === tenantId) ?? null;
  const businessDirectory = useMemo(
    () => {
      if (userBusinessesState.data === undefined && !userBusinessesState.isError) return [];
      return buildBusinessDirectory(userTenants, userBusinessesState.data ?? []);
    },
    [userBusinessesState.data, userBusinessesState.isError, userTenants],
  );

  async function selectDirectoryBusiness(entry: BusinessDirectoryEntry): Promise<void> {
    const changesTenant = entry.tenantId !== profile?.active_tenant_id;
    if (changesTenant) await switchTenant(entry.tenantId);
    if (entry.businessId && (changesTenant || entry.businessId !== profile?.active_business_id)) {
      await switchBusiness(entry.businessId);
    }
  }

  // T-012: reminder "Completa dati locale" per i ristoratori.
  // Stesso queryKey della BusinessDetailsPage → cache condivisa.
  const { data: tenantAnagrafica } = useQuery<TenantLite | null>({
    queryKey: ['tenant_anagrafica', tenantId],
    enabled: !!tenantId && profile?.role === 'ristoratore',
    staleTime: 60_000,
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('id, responsabile_haccp_name')
        .eq('id', tenantId)
        .maybeSingle();
      if (error) throw error;
      return (data as TenantLite | null) ?? null;
    },
  });

  const { data: recentDocs = [] } = useQuery({
    queryKey: ['today_recent_docs', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const sevenDaysAgo = new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
      const sb = getSupabase();
      const merged: Array<{ id: string; source: 'fiscal' | 'public'; type: string; number: string; date: string; supplier_id: string | null; total_amount: number | null; status: string; supplier_name?: string | null }> = [];

      const { data: fatture } = await sb
        .from('fiscal_documents')
        .select('id,type,number,date,supplier_id,total_amount,status')
        .gte('date', sevenDaysAgo)
        .order('date', { ascending: false })
        .limit(5);
      for (const d of (fatture ?? []) as Array<{ id: string; type: string; number: string; date: string; supplier_id: string | null; total_amount: number | null; status: string }>) {
        merged.push({ ...d, source: 'fiscal' });
      }

      const { data: ddt } = await sb
        .from('documents')
        .select('id,kind,document_number,document_date,supplier_id,created_at')
        .gte('created_at', new Date(Date.now() - 7 * 86_400_000).toISOString())
        .order('created_at', { ascending: false })
        .limit(5);
      for (const d of (ddt ?? []) as Array<{ id: string; kind: string; document_number: string | null; document_date: string | null; supplier_id: string | null; created_at: string }>) {
        merged.push({
          id: d.id,
          source: 'public',
          type: 'ddt',
          number: d.document_number ?? '—',
          date: d.document_date ?? d.created_at.slice(0, 10),
          supplier_id: d.supplier_id,
          total_amount: null,
          status: 'received',
        });
      }

      merged.sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
      const top = merged.slice(0, 5);

      const supIds = [...new Set(top.map((d) => d.supplier_id).filter((x): x is string => !!x))];
      const supMap: Record<string, string> = {};
      if (supIds.length) {
        const { data: srows } = await sb.from('suppliers').select('id,name').in('id', supIds);
        for (const s of (srows ?? []) as { id: string; name: string }[]) supMap[s.id] = s.name;
      }
      return top.map((d) => ({ ...d, supplier_name: d.supplier_id ? supMap[d.supplier_id] ?? null : null }));
    },
  });

  const foodById = new Map(foods.map((food) => [food.id, food]));
  const expirationCandidates = stockProjection.data.balances
    .filter((balance) => balance.onHandQty > 0 && !!balance.expirationDate)
    .map((balance) => {
      const food = foodById.get(balance.foodId);
      return {
        id: balance.key,
        foodId: balance.foodId,
        expirationDate: balance.expirationDate,
        isPreparation: !!food?.recipe_id,
        product: food?.name ?? balance.foodId.slice(0, 8),
        date: balance.expirationDate!,
      };
    });
  const notificationLeadHours = Math.max(0, ...expirationPreferences.reminderLeadHours);
  const expirationNotifications = selectActionableExpirationAlerts(
    notificationLeadHours > 0
      ? expirationCandidates.filter((candidate) => (
        isWithinExpirationLead(candidate.expirationDate, notificationLeadHours)
      ))
      : [],
  );

  const expirationNotificationKey = [
    profile?.active_business_id ?? 'device',
    expirationPreferences.reminderLeadHours.join(','),
    ...expirationNotifications.map((expiration) => expiration.id),
  ].join('|');
  useEffect(() => {
    if (expirationNotifications.length > 0) {
      void notifyExpirations(
        expirationNotifications,
        expirationPreferences,
        profile?.active_business_id ?? null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expirationNotificationKey]);

  const suggestions = useAiSuggestions();

  const go = (path: string) => history.push(path);

  // T-227: salva la config dashboard del cliente (tenant attivo). Il toast
  // dedicato evita di intrecciarsi con eventuali altri toast della pagina.
  const [dashToast, setDashToast] = useState<string | null>(null);
  async function saveDashboardConfig(): Promise<void> {
    setSavingDashboard(true);
    try {
      await setDashboardConfig.mutateAsync({ expected: dashboardConfig, next: draftConfig });
      setDashToast('Dashboard aggiornata');
      setEditorOpen(false);
    } catch (e) {
      setDashToast(toUserMessage(e));
    } finally {
      setSavingDashboard(false);
    }
  }

  const firstName = profile?.full_name?.split(' ')[0] ?? '';
  const alertsTyped = alerts as ProbeAlert[];
  // T-249: mostra solo le allerte sonda davvero attive (unità non archiviata,
  // recenti, una per unità) invece del conteggio grezzo degli eventi immutabili
  // — altrimenti un'allerta di giorni prima o di un frigo archiviato resta accesa.
  const activeAlerts = useMemo(
    () => selectActiveProbeAlerts(alertsTyped, units, Date.now()),
    [alertsTyped, units],
  );

  // T-019: countdown del trial. Visibile solo quando rimangono ≤ 3 giorni
  // (focused warning, non rumore di fondo). L'owner non lo vede mai. Se il
  // trial è scaduto, AuthGate ha già reindirizzato a /trial-expired prima
  // che TodayPage venga renderizzata.
  const trialDaysLeft: number | null = (() => {
    if (!profile?.trial_ends_at) return null;
    if (profile.is_platform_owner) return null;
    const ends = new Date(profile.trial_ends_at).getTime();
    if (!Number.isFinite(ends)) return null;
    const ms = ends - Date.now();
    if (ms <= 0) return null;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  })();
  const showTrialCountdown = trialDaysLeft !== null && trialDaysLeft <= 3;
  const trialWaUrl = (() => {
    const greeting = profile?.full_name ? `Ciao, sono ${profile.full_name}.` : 'Ciao,';
    const txt = encodeURIComponent(
      `${greeting} Stanno per finire i giorni di prova di TrackEAT, vorrei attivare l'abbonamento.`,
    );
    return `https://wa.me/393486382343?text=${txt}`;
  })();

  // T-012: calcolo "campi locale mancanti" per il reminder.
  // Considerati minimi: P.IVA + indirizzo completo (via, città, CAP, provincia)
  // + Responsabile HACCP. Manca almeno uno → reminder visibile.
  const missingBusinessData: string[] = (() => {
    if (profile?.role !== 'ristoratore') return [];
    // Se le query non sono ancora risolte, evitiamo flicker mostrando il
    // reminder solo quando i dati sono effettivamente arrivati.
    if (!activeBusiness) return [];
    const out: string[] = [];
    if (!activeBusiness.piva) out.push('P.IVA');
    if (!activeBusiness.address) out.push('indirizzo');
    if (!activeBusiness.city) out.push('città');
    if (!activeBusiness.cap) out.push('CAP');
    if (!activeBusiness.provincia) out.push('provincia');
    // responsabile_haccp_name è sul tenant; se la query è disabilitata o non
    // ha ancora risposto, evitiamo di segnalarlo come mancante per evitare
    // falsi positivi.
    if (tenantAnagrafica && !tenantAnagrafica.responsabile_haccp_name) {
      out.push('responsabile HACCP');
    }
    return out;
  })();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('today.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        <IonRefresher
          slot="fixed"
          onIonRefresh={async (ev) => {
            await getSyncEngine().tick().catch(() => undefined);
            ev.detail.complete();
          }}
        >
          <IonRefresherContent />
        </IonRefresher>

        <PageGreeting greeting={t('today.greeting')} name={firstName} />

        {/* T-325: modello AI configurato per l'azienda attiva. "TrackEAT IA" =
            standard; "TrackEAT IA PRO" = pro a pagamento; "Altro - X" = scelta
            specifica da dashboard. Allineato con la dashboard admin. */}
        <div style={{ padding: '0 16px 0', marginTop: -2 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--ion-color-medium)' }}>
            🤖 Modello AI:&nbsp;
            <b style={{ color: aiModelKind(aiProvider) === 'pro' ? 'var(--ion-color-success)' : 'var(--ion-color-medium)' }}>
              {aiModelLabel(aiProvider)}
            </b>
          </span>
        </div>

        <div style={{ padding: '8px 16px 24px' }}>
          {/* T-103/T-223/T-594: banner di contesto. Due selettori distinti:
              - ATTIVITÀ E LOCALI: directory ricercabile per consulente/owner.
              - LOCALE (business): per QUALSIASI ruolo, quando l'azienda attiva ha
                più di una sede e non usa già la directory completa. */}
          {(isMultiTenant || isMultiBusiness) && (
            <div style={{
              background: 'var(--ion-card-background, #fff)',
              border: '1px solid var(--ion-color-step-200, #dbe3ec)',
              borderRadius: 12,
              padding: '10px 14px',
              marginBottom: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              {isMultiTenant && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IonIcon icon={businessOutline} aria-hidden="true" style={{ fontSize: 21, color: 'var(--ion-color-primary)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ion-color-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Attività e locali
                    </div>
                    <button
                      type="button"
                      className="te-business-directory-trigger"
                      aria-label="Cambia locale. Apri ricerca e filtri per zona"
                      onClick={() => setBusinessDirectoryOpen(true)}
                    >
                      <span>
                        <span className="te-business-directory-trigger-name">
                          {activeTenant?.name || activeBusiness?.name || 'Seleziona locale'}
                        </span>
                        {activeBusiness?.name && activeTenant?.name && activeBusiness.name !== activeTenant.name && (
                          <span className="te-business-directory-trigger-detail">{activeBusiness.name}</span>
                        )}
                      </span>
                      <IonIcon icon={chevronForward} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
              {isMultiBusiness && !isMultiTenant && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IonIcon icon={storefrontOutline} aria-hidden="true" style={{ fontSize: 21, color: 'var(--ion-color-primary)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ion-color-primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Locale attivo
                    </div>
                    <IonSelect
                      interface="action-sheet"
                      value={profile?.active_business_id ?? ''}
                      onIonChange={(ev) => {
                        const id = String(ev.detail.value ?? '');
                        if (id && id !== profile?.active_business_id) void switchBusiness(id);
                      }}
                      style={{
                        '--padding-start': 0,
                        '--padding-end': 0,
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--ion-text-color, #0f172a)',
                      } as React.CSSProperties}
                    >
                      {tenantBusinesses.map((b) => (
                        <IonSelectOption key={b.id} value={b.id}>
                          {b.name || `Locale ${b.id.slice(0, 8)}`}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>
                </div>
              )}
            </div>
          )}

          <BusinessDirectoryModal
            isOpen={businessDirectoryOpen}
            entries={businessDirectory}
            activeTenantId={profile?.active_tenant_id ?? null}
            activeBusinessId={profile?.active_business_id ?? null}
            loading={userTenantsState.isFetching || userBusinessesState.isFetching}
            loadError={userTenantsState.isError || userBusinessesState.isError}
            onRetry={() => {
              void userTenantsState.refetch();
              void userBusinessesState.refetch();
            }}
            onClose={() => setBusinessDirectoryOpen(false)}
            onSelect={selectDirectoryBusiness}
          />

          {/* Allerte: in cima, accent rosso, cliccabile per andare alle temperature */}
          {activeAlerts.length > 0 && (
            <StatCard accent="rose" onClick={() => go('/haccp/temperatures')}>
              <StatBadge count={activeAlerts.length} label={activeAlerts.length === 1 ? 'allerta attiva' : 'allerte attive'} tone="danger" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {activeAlerts.slice(0, 3).map((a) => {
                  const unit = units.find((u) => u.id === a.payload.storage_unit_id);
                  return (
                    <div key={a.id} style={{ fontSize: 13, color: palette.textMuted, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <strong style={{ color: palette.text, fontWeight: 600 }}>
                        {unit?.name ?? a.payload.storage_unit_id.slice(0, 8)}
                      </strong>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.payload.message}
                      </span>
                      <IonBadge color={a.payload.severity === 'critical' ? 'danger' : 'warning'} style={{ fontSize: 10 }}>
                        {a.payload.severity}
                      </IonBadge>
                    </div>
                  );
                })}
              </div>
            </StatCard>
          )}

          {/* T-019: countdown trial — visibile solo quando rimangono ≤ 3
              giorni. Tap → WhatsApp pre-compilato per attivare l'abbonamento. */}
          {showTrialCountdown && trialDaysLeft !== null && (
            <StatCard
              accent="amber"
              onClick={() => {
                window.open(trialWaUrl, '_blank', 'noopener');
              }}
            >
              <StatBadge
                count={trialDaysLeft}
                label={trialDaysLeft === 1 ? 'giorno di prova rimasto' : 'giorni di prova rimasti'}
                tone="warn"
              />
              <div style={{ fontSize: 13, color: palette.text, fontWeight: 600, marginTop: 4 }}>
                Stanno per finire i giorni di prova
              </div>
              <div style={{ fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
                Tocca per scriverci su WhatsApp e attivare l'abbonamento, così l'app continua a funzionare senza interruzioni.
              </div>
            </StatCard>
          )}

          {/* T-012: reminder ristoratore — dati locale incompleti.
              Visibile solo se role=ristoratore e mancano campi minimi.
              Tap → /settings/business. Niente dismiss: il reminder sparisce
              da solo quando i dati sono completi. */}
          {missingBusinessData.length > 0 && (
            <StatCard accent="amber" onClick={() => go('/settings/business')}>
              <StatBadge
                count={missingBusinessData.length}
                label={missingBusinessData.length === 1 ? 'campo mancante nel locale' : 'campi mancanti nel locale'}
                tone="warn"
              />
              <div style={{ fontSize: 13, color: palette.text, fontWeight: 600, marginTop: 4 }}>
                Completa i dati del locale
              </div>
              <div style={{ fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
                Serve per il Report ASL e le etichette di tracciabilità.
              </div>
              <div style={{ fontSize: 12, color: palette.textMuted, marginTop: 6, fontStyle: 'italic' }}>
                Manca: {missingBusinessData.join(', ')}
              </div>
            </StatCard>
          )}

          {canConfigurePricing && previewRole === null && (
            <div data-testid="consultant-pricing-dashboard-tile">
              <SectionHeader title="Strumenti consulente" />
              <TileGrid style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                <HeroTile
                  icon="€"
                  title="Configura offerta"
                  subtitle="Pacchetti, moduli e dispositivi"
                  gradient="teal"
                  onClick={() => go('/consultant/pricing')}
                />
              </TileGrid>
            </div>
          )}

          {/* T-227: tile data-driven (chiave stabile per ognuno) + filtro per
              flag (collettiva) e per visibilità di ruolo (dashboard_config).
              Il consulente/owner vede tutto e può aprire "Modifica dashboard"
              per accendere/spegnere i tile per ciascun ruolo. */}
          <SectionHeader title="Azioni veloci" />
          {canConfigureOperationalProfiles && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, margin: '0 0 10px' }}>
              <button
                type="button"
                onClick={() => { setDraftConfig(dashboardConfig); setEditorRole('ristoratore'); setEditorOpen(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  background: '#fff', border: '1px dashed #93c5fd', borderRadius: 999,
                  color: '#0066cc', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                }}
              >
                👁️ Cosa vede il personale
              </button>
              <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 2 }}>Vedi come:</span>
              {configurableSubjects.map((r) => {
                const active = previewRole === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setPreviewRole(active ? null : r.value)}
                    style={{
                      padding: '5px 11px', borderRadius: 999, cursor: 'pointer',
                      fontSize: 12, fontWeight: 700,
                      background: active ? '#0066cc' : '#fff',
                      color: active ? '#fff' : '#475569',
                      border: `1px solid ${active ? '#0066cc' : '#cbd5e1'}`,
                    }}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          )}
          {previewRole && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              margin: '0 0 12px', padding: '8px 12px',
              background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 12,
              fontSize: 12.5, color: '#9a3412',
            }}>
              <span>👁️ Anteprima Home come <b>{configurableSubjects.find((r) => r.value === previewRole)?.label}</b> — i quadrati nascosti per questo profilo spariscono. Le schede in basso e le voci Impostazioni si verificano col login reale.</span>
              <button type="button" onClick={() => setPreviewRole(null)} style={{
                flexShrink: 0, padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                fontSize: 12, fontWeight: 700, background: '#9a3412', color: '#fff', border: 'none',
              }}>Esci</button>
            </div>
          )}
          <div className="te-dashboard-tiles">
            {visibleTilePairs.map((pair, i) => (
              <TileGrid key={pair.map((t) => t.key).join('-')} style={i > 0 ? { marginTop: 12 } : undefined}>
                {pair.map((tile) => (
                  <HeroTile
                    key={tile.key}
                    icon={tile.icon}
                    title={tile.title}
                    subtitle={tile.subtitle}
                    gradient={tile.gradient}
                    onClick={() => go(tile.route)}
                  />
                ))}
              </TileGrid>
            ))}
          </div>

          {/* Documenti recenti */}
          <SectionHeader title="Ultimi documenti" />
          <FeatureCard
            featureKey="documents"
            icon="📄"
            title="Documenti di carico"
            count={recentDocs.length}
            countLabel={recentDocs.length === 1 ? 'documento ultimi 7 giorni' : 'documenti ultimi 7 giorni'}
            description={
              <>
                Carica i tuoi <b>DDT e fatture</b> con una foto o un PDF:
                l'AI estrae automaticamente fornitore, righe, lotti,
                scadenze e quantità. Tu controlli e confermi.
                <br /><br />
                Ogni documento finisce nell'archivio fiscale 10 anni
                (obbligo di legge) e i lotti entrano subito nel
                magazzino con tracciabilità completa.
              </>
            }
            whatsappQuestion="Ciao, ho dubbi su come caricare DDT/fatture in TrackEAT, mi puoi aiutare?"
            userId={profile?.id ?? null}
            businessId={profile?.active_business_id ?? null}
            onStart={() => go('/documents')}
          >
            {recentDocs.slice(0, 5).map((d) => (
              <StatRow
                key={`${d.source}:${d.id}`}
                label={d.supplier_name ?? '— Fornitore non riconosciuto —'}
                meta={`${d.type.toUpperCase()} ${d.number} · ${new Date(d.date).toLocaleDateString('it-IT')}${d.total_amount != null ? ` · €${d.total_amount.toFixed(2)}` : ''}`}
                value={
                  d.status !== 'reconciled' && d.status !== 'matched' && d.status !== 'received' ? (
                    <IonBadge color="warning" style={{ fontSize: 10 }}>{d.status}</IonBadge>
                  ) : undefined
                }
                onClick={(e) => {
                  e.stopPropagation();
                  go(`/documents/${d.source}/${d.id}`);
                }}
              />
            ))}
          </FeatureCard>
        </div>

        <AiSuggestionsFab suggestions={suggestions} />

        {/* T-227/T-690: l'editor mostra soltanto le superfici incluse nei moduli
            attivi. Le preferenze dei moduli spenti restano nel config e tornano
            disponibili se il contratto viene riattivato. */}
        <IonModal isOpen={editorOpen} onDidDismiss={() => setEditorOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Cosa vede il personale</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setEditorOpen(false)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
            <IonToolbar>
              <IonItem lines="none">
                <IonLabel>Profilo da configurare</IonLabel>
                <IonSelect
                  value={editorRole}
                  interface="popover"
                  onIonChange={(ev) => setEditorRole(String(ev.detail.value ?? 'ristoratore'))}
                >
                  {configurableSubjects.map((subject) => (
                    <IonSelectOption key={subject.value} value={subject.value}>
                      {subject.label}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding" style={{ '--background': '#eef2f7' } as React.CSSProperties}>
            <p style={{ fontSize: 13, color: '#475569', margin: '0 4px 12px' }}>
              Scegli cosa vede <b>{configurableSubjects.find((r) => r.value === editorRole)?.label}</b>:
              quadrati della Home, schede in basso e voci di Impostazioni. Le modifiche valgono solo per questo cliente.
              Oggi e Impostazioni restano sempre visibili. Le funzioni non incluse nel contratto non compaiono qui.
            </p>
            {configurableVisibilitySections.map((section) => (
              <div key={section.id} style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 4px 6px' }}>
                  {section.title}
                </h3>
                <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden' }}>
                  {section.items.map((item) => {
                    const subject = configurableSubjects.find((candidate) => candidate.value === editorRole);
                    const visible = isSurfaceVisibleForSubjects(
                      draftConfig,
                      [editorRole, ...(subject?.fallbackSubjects ?? [])],
                      item.key,
                    );
                    return (
                      <IonItem key={item.key} lines="full">
                        <span slot="start" style={{ fontSize: 20 }}>{item.icon}</span>
                        <IonLabel>
                          <h3 style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</h3>
                        </IonLabel>
                        <IonToggle
                          slot="end"
                          checked={visible}
                          onIonChange={(ev) =>
                            setDraftConfig((cfg) => editorRole.startsWith('person:')
                              ? setPersonalSurfaceVisibility(
                                  cfg,
                                  editorRole,
                                  subject?.fallbackSubjects ?? ['staff'],
                                  item.key,
                                  !!ev.detail.checked,
                                )
                              : setSurfaceVisibility(cfg, editorRole, item.key, !!ev.detail.checked))
                          }
                        />
                      </IonItem>
                    );
                  })}
                </div>
              </div>
            ))}
            <IonButton
              expand="block"
              style={{ marginTop: 16 }}
              disabled={savingDashboard}
              onClick={() => { void saveDashboardConfig(); }}
            >
              {savingDashboard ? <IonSpinner name="dots" /> : 'Salva'}
            </IonButton>
            <p style={{ fontSize: 11.5, color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>
              Il consulente vede sempre tutti i quadrati dei moduli attivi. Questa configurazione
              riguarda Ristoratore, OSA, Staff e le singole persone. Sul tablet
              condiviso si applica la persona selezionata con PIN.
            </p>
          </IonContent>
        </IonModal>

        <IonToast isOpen={!!dashToast} message={dashToast ?? ''} duration={2200} onDidDismiss={() => setDashToast(null)} />
      </IonContent>
    </IonPage>
  );
}
